import { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import isObject from 'lodash/isObject'
import { SelectOption } from '@microservices/wiskey-react-components'

import { getDataFromState, getStateFromData } from '@components/CommandParameters/helpers'

import { useFetchAllFormQuery, useFetchDictionariesByCategoryCodeQuery } from '@redux/api'
import { useFetchAllObjectQuery } from '@redux/api/objects.api'
import { useFetchAllVariablesQuery } from '@redux/api/variables.api'

import { usePrompt } from '@hooks'
import {
  ACTION_CODE,
  CATEGORIES,
  ENTITY_TYPES_REQUEST,
  EVENT_CODE,
  MODAL_TYPE,
  MODEL_TYPE,
} from '@constants'
import { AutocompleteOption } from '@types'

import { PageContext } from '../../../FormCreateOrEdit'
import { getDefaultValue } from '../helpers'

import { ActionForm, useInputs } from './useInputs'

export const useAddActionDialog = (objectCode: string) => {
  const defaultValues: ActionForm = getDefaultValue()
  const methods = useForm<ActionForm>({ defaultValues })
  const [isOpenScriptValueDialog, setIsOpenScriptValueDialog] = useState(false)
  const [currentNameScriptDialog, setCurrentNameScriptDialog] = useState('')
  const [isLoading, setLoading] = useState(true)
  const [showFormActionCommandsModal, setShowFormActionCommandsModal] = useState<boolean>(false)
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
    setValue,
    trigger,
  } = methods

  const watchJson = watch('json')
  const watchJsValue = watch('jsValue')
  const watchActionCode = watch('actionCode')
  const watchEvent = watch('eventCode')
  const watchFormObject = watch('formObject')
  const watchVariableOption = watch('variable')
  const watchRedifinitionCommands = watch('redefinitionCommands')

  const categoryCode: CATEGORIES = CATEGORIES.BINDING_FORM_EVENTS_ACTION
  const isFormActionCode =
    watchActionCode === ACTION_CODE.OPEN_FORM_MODEL ||
    watchActionCode === ACTION_CODE.OPEN_ANOTHER_FORM_MODEL

  usePrompt({ when: isDirty })

  const {
    data: bindingValuesActionEvents,
    isLoading: isLoadingBindingValuesEvents,
    isFetching: isFetchingBindingValuesEvents,
  } = useFetchDictionariesByCategoryCodeQuery(categoryCode)

  const loadingBindingValuesEvents = isLoadingBindingValuesEvents || isFetchingBindingValuesEvents

  const {
    createAction,
    currentRow,
    onCloseModal,
    modalType,
    editAction,
    onSetModalProperty,
    formCode,
    bindingValuesAction,
    currentForm,
  } = useContext(PageContext)

  const fieldsOptions: AutocompleteOption[] = useMemo(
    () =>
      currentForm?.fields?.map(field => ({
        id: field.code,
        label: field.title,
      })) || [],
    [currentForm]
  )

  const { data: variablesData, isFetching: isLoadingVariables } = useFetchAllVariablesQuery(
    {
      viewType: currentForm?.id ? ENTITY_TYPES_REQUEST.FORM : undefined,
      id: currentForm?.id ? currentForm?.id : undefined,
    },
    { skip: !currentForm?.id }
  )

  const variablesOptions: AutocompleteOption[] = useMemo(
    () =>
      variablesData?.map(variable => ({
        id: variable?.id,
        label: variable?.title,
      })) || [],
    [variablesData]
  )

  const {
    data: forms,
    isFetching: isFetchingAllForm,
    isLoading: isLoadingAllForm,
  } = useFetchAllFormQuery(
    {
      objectCode: (watchFormObject?.id as string) ?? '',
    },
    {
      skip: !watchFormObject,
    }
  )

  const { data: objects, isLoading: isAllObjectsLoading } = useFetchAllObjectQuery(undefined)

  const loadingAllForm = isFetchingAllForm || isLoadingAllForm

  const formCodeOptions: AutocompleteOption[] = useMemo(
    () =>
      forms?.data.map(form => ({
        id: form.code,
        label: form.title,
      })) ?? [],
    [forms]
  )

  const actionTypeOptions: SelectOption[] = useMemo(() => {
    const options =
      bindingValuesAction?.map(cat => ({
        id: cat.id,
        name: cat.code,
      })) || []

    if (!options.some(option => option.name === ACTION_CODE.PRINT_FORM_VOU)) {
      options.push({
        id: ACTION_CODE.PRINT_FORM_VOU,
        name: ACTION_CODE.PRINT_FORM_VOU,
      })
    }

    return options
  }, [bindingValuesAction])

  const eventOptions: SelectOption[] | null = useMemo(() => {
    const options =
      bindingValuesActionEvents?.map(event => ({
        id: event.id,
        name: event.code,
      })) || []

    if (
      watchActionCode === ACTION_CODE.EXECUTE_COMMAND ||
      watchActionCode === ACTION_CODE.PRINT_FORM_VOU
    ) {
      return options?.filter(option => option.name == EVENT_CODE.ON_BUTTON_CLICK)
    }

    if (watchActionCode !== ACTION_CODE.CHANGE_VARIABLE_VALUE) {
      return options?.filter(option => option.name !== ACTION_CODE.CHANGE_VARIABLE_VALUE)
    }

    return options
  }, [bindingValuesActionEvents, watchActionCode])

  const objectOptions = useMemo(() => {
    if (!objects) {
      return []
    }

    if (watchActionCode === ACTION_CODE.OPEN_FORM_MODEL) {
      const entityObject = objects.find(object => object.code === objectCode)

      if (!entityObject) {
        return []
      }

      return [{ id: entityObject.code, label: entityObject.title }]
    }

    return objects.map(object => {
      return {
        id: object.code,
        label: object.title,
      }
    })
  }, [objects, watchActionCode])

  useEffect(() => {
    if (currentRow) {
      reset({
        ...currentRow,
        params: currentRow.params ? JSON.stringify(currentRow.params) : '',
        order: currentRow.order?.toString(),
        jsValue: currentRow.jsValue || '',
        field:
          currentRow?.objectFieldCode && currentRow?.objectFieldTitle
            ? {
                id: currentRow.objectFieldCode,
                label: currentRow.objectFieldTitle,
              }
            : null,
        variable: currentRow?.variable
          ? { id: currentRow?.variable?.id, label: currentRow?.variable?.title?.toString() }
          : null,
        formObject: currentRow?.objectCode
          ? {
              id: currentRow?.objectCode,
              label: currentRow?.objectTitle,
            }
          : null,
        objectFormCode: currentRow?.objectFormCode
          ? {
              id: currentRow?.objectFormCode,
              label: currentRow?.objectFormTitle,
            }
          : null,
        json: Boolean(currentRow.params),
        targetColumn: currentRow.target
          ? { id: currentRow.target.id, label: currentRow.target.title }
          : null,
        columnToSort: null,
        command: currentRow.command
          ? [
              {
                ...currentRow.command,
                name: {
                  id: currentRow.command.name,
                  label: currentRow.command.name,
                },
                sourceCommand: {
                  id: currentRow.command.objectCode,
                  label: currentRow.command.objectTitle,
                },
                sourceType: {
                  id: currentRow.command.resource,
                  label: currentRow.command.resource,
                },
                type: null,
                parameters: getStateFromData(currentRow.command.parameters),
                body: getStateFromData(currentRow.command.body),
              },
            ]
          : null,
        redefinitionCommands: currentRow.redefinitionCommands?.map(command => {
          return {
            ...command,
            name: {
              id: command.name,
              label: command.name,
            },
            sourceCommand: {
              id: command?.objectCode,
              label: command?.objectTitle,
            },
            sourceType: {
              id: command?.resource,
              label: command?.resource,
            },
            type: { id: command.type, label: command.type },
            parameters: getStateFromData(command.parameters),
            body: getStateFromData(command.body),
          }
        }),
      })

      setLoading(false)
    }
  }, [currentRow])

  useEffect(() => {
    onSetModalProperty?.(MODEL_TYPE.FORM_ACTION)
  }, [])

  const handleShowFormActionCommandsModal = () => setShowFormActionCommandsModal(true)
  const handleCloseFormActionCommandsModal = () => {
    trigger('redefinitionCommands').then(value => {
      if (value) {
        setShowFormActionCommandsModal(false)
      }
    })
  }

  const handleSave = handleSubmit((data: ActionForm) => {
    const {
      id = Date.now(),
      code,
      title,
      actionCode = ACTION_CODE.OPEN_FORM_MODEL,
      eventCode = EVENT_CODE.ON_BUTTON_CLICK,
      params = '',
      formObject,
      objectFormCode,
      json,
      targetColumn,
      command,
      redefinitionCommands,
      field,
    } = data

    if (modalType === MODAL_TYPE.EDIT && currentRow) {
      editAction?.({
        code,
        title,
        eventCode,
        jsValue: watchJsValue || undefined,
        actionCode,
        variableId: watchVariableOption?.id,
        objectFieldCode: field ? field.id : undefined,
        objectFieldTitle: field ? field.label : undefined,
        id,
        formCode,
        targetId: targetColumn?.id,
        command: command?.length
          ? {
              ...command[0],
              name: command[0].name?.id,
              objectTitle: command[0].sourceCommand?.label,
              objectCode: command[0].sourceCommand?.id,
              resource: command[0].sourceType?.id,
              type: command[0].type?.id,
              parameters: getDataFromState(command[0].parameters),
              body: getDataFromState(command[0].body),
            }
          : null,
        ...(json
          ? { params }
          : {
              objectCode:
                isObject(formObject) && formObject?.id ? formObject.id.toString() : undefined,
              objectTitle:
                isObject(formObject) && formObject?.label ? formObject.label.toString() : undefined,
              objectFormCode:
                isObject(objectFormCode) && objectFormCode?.id
                  ? objectFormCode.id.toString()
                  : undefined,
              redefinitionCommands: redefinitionCommands?.map(command => {
                return {
                  ...command,
                  name: command.name?.id,
                  objectCode: command.sourceCommand?.id,
                  objectTitle: command.sourceCommand?.label,
                  type: command.type?.id,
                  resource: command.sourceType?.id,
                  parameters: getDataFromState(command.parameters),
                  body: getDataFromState(command.body),
                }
              }),
            }),
      })
        .unwrap()
        .then(() => {
          onCloseModal()
        })
        .catch(console.error)

      return
    }
    createAction?.({
      code,
      title,
      eventCode,
      actionCode,
      variableId: watchVariableOption?.id,
      objectFieldCode: field ? field.id : undefined,
      jsValue: watchJsValue || undefined,
      objectFieldTitle: field ? field.label : undefined,
      id,
      targetId: targetColumn?.id,
      command: command?.length
        ? {
            ...command[0],
            name: command[0].name?.id,
            objectTitle: command[0].sourceCommand?.label,
            type: command[0].type?.id,
            objectCode: command[0].sourceCommand?.id,
            resource: command[0].sourceType?.id,
            parameters: getDataFromState(command[0].parameters),
            body: getDataFromState(command[0].body),
          }
        : null,
      formCode,
      ...(json
        ? { params }
        : {
            objectCode:
              isObject(formObject) && formObject?.id ? formObject.id.toString() : undefined,
            objectTitle:
              isObject(formObject) && formObject?.label ? formObject.label.toString() : undefined,
            objectFormCode:
              isObject(objectFormCode) && objectFormCode?.id
                ? objectFormCode.id.toString()
                : undefined,
            redefinitionCommands: redefinitionCommands?.map(command => {
              return {
                ...command,
                name: command.name?.id,
                objectCode: command.sourceCommand?.id,
                resource: command.sourceType?.id,
                objectTitle: command.sourceCommand?.label,
                type: command.type?.id,
                parameters: getDataFromState(command.parameters),
                body: getDataFromState(command.body),
              }
            }),
          }),
    })
      .unwrap()
      .then(() => {
        onCloseModal()
        reset(defaultValues)
      })
      .catch(console.error)
  })

  const { actionInputs, actionValueInputs, actionJSONValueInputs } = useInputs({
    setValue,
    setIsOpenScriptValueDialog,
    setCurrentNameScriptDialog,
    actionTypeOptions,
    eventOptions,
    objectOptions,
    watchJson,
    watchEvent,
    fieldsOptions,
    variablesOptions,
    watchActionCode,
    watchFormObject,
    formCodeOptions,
    loadingAllForm,
    loadingBindingValuesEvents,
    isAllObjectsLoading,
    modalType,
  })

  return {
    methods,
    isDirty,
    watchFormObject,
    watchJson,
    isFormActionCode,
    watchRedifinitionCommands,
    watchJsValue,
    actionInputs,
    actionValueInputs,
    actionJSONValueInputs,
    isLoading,
    showFormActionCommandsModal,
    isOpenScriptValueDialog,
    currentNameScriptDialog,
    setValue,
    handleSave,
    handleShowFormActionCommandsModal,
    handleCloseFormActionCommandsModal,
    setIsOpenScriptValueDialog,
  }
}
